import { FILE_OPTIONS, DEMAND, DEMAND_TYPE } from "@demandproai/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import usePfqAxios from "./usePfqAxios";
import { useDownloadOptions } from "./useDownloadOptions";
import useDocxVersioning from "./useDocxVersioning";
import Constants from "../Constants";
import { toaster } from "../utils/toast";

const CASE_FILES_KEY = FILE_OPTIONS.CASE_FILES;
const MEDICAL_CHRONOLOGY_KEY = FILE_OPTIONS.MEDICAL_CHRONOLOGY;
const SILENT_WITNESS_KEY = FILE_OPTIONS.SILENT_WITNESS;

export const useVersionHistory = ({
  caseData,
  isOpen,
  onClose,
  onDownloadComplete,
}) => {
  const { _id, caseInfo, clients = [], docxFiles = {}, s3LifecarePlanZipUrlPath } = caseData;
  const caseName = caseInfo.caseName;

  const { postData } = usePfqAxios();
  const {
    downloadDocxVersion,
    downloadCaseFilesVersion,
    downloadLifecarePlanVersion,
    downloadCertifiedLifecarePlanVersion,
    downloadOptions,
  } = useDownloadOptions(caseData);
  const { getDocxS3Url, formatVersionLabel } = useDocxVersioning({ caseData });

  const [selectedFileKey, setSelectedFileKey] = useState(null);
  const [selectedLifecareClientId, setSelectedLifecareClientId] = useState(null);
  const [versionsMap, setVersionsMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const lifecareClients = useMemo(() => {
    return clients
      .filter((client) => client.result?.lifecarePlan?.s3FilePath)
      .map((client) => ({
        clientId: client.clientId,
        clientName: client.personalInfo.clientName || client.clientId,
        s3FilePath: client.result.lifecarePlan.s3FilePath,
      }));
  }, [clients]);

  const demands = useMemo(
    () => {
      if (s3LifecarePlanZipUrlPath) return downloadOptions.filter((v) => v !== DEMAND.Lifecare_Plan);
      if (lifecareClients.length > 0) return downloadOptions;
      return downloadOptions.filter((v) => v !== DEMAND.Lifecare_Plan);
    },
    [downloadOptions, lifecareClients, s3LifecarePlanZipUrlPath],
  );

  const buildFileOptionsMap = useCallback(
    ({ versionsData }) => {
      const map = {};
      map[CASE_FILES_KEY] = versionsData.caseFiles?.versions || [];
      map[MEDICAL_CHRONOLOGY_KEY] = versionsData.medical_chronology?.versions || [];
      map[SILENT_WITNESS_KEY] = versionsData.silent_witness?.versions || [];

      demands.forEach((v) => {
        if (v !== DEMAND.Lifecare_Plan) {
          const docxKey = DEMAND_TYPE?.[v]?.key;
          map[v] = versionsData?.[docxKey]?.versions || [];
        }
      });

      lifecareClients.forEach((c) => {
        map[`lifecare_${c.clientId}`] =
          versionsData?.[`lifecare_${c.clientId}`]?.versions || [];
      });

      return map;
    },
    [demands, lifecareClients],
  );

  useEffect(() => {
    if (!isOpen) return;

    setSelectedFileKey(null);
    setSelectedLifecareClientId(null);
    setIsLoading(true);
    postData(Constants.ApiUrl.case.getCaseFileVersions, { caseId: _id }).then(
      ({ success, data }) => {
        const versionsData =
          success && data?.versionsData ? data.versionsData : {};
        setVersionsMap(buildFileOptionsMap({ versionsData }));
        setIsLoading(false);
      },
    ).catch(
      (err) => {
        console.log(err);
        setIsLoading(false);
        toaster({ message: 'Failed to load version history', success: false });
      }
    );
  }, [isOpen, _id, buildFileOptionsMap, postData]);

  const handleSelectFile = (key) => {
    setSelectedFileKey(key);
    setSelectedLifecareClientId(null);
  };

  const handleDownload = async ({ fileKey, versionNumber, lifecareClientId }) => {
    try {
      if (fileKey === CASE_FILES_KEY) {
        await downloadCaseFilesVersion({
          caseId: _id,
          caseName,
          versionNumber,
          onDownloaded: onDownloadComplete,
        });
        return;
      }

      if (fileKey === DEMAND.Certified_Lifecare_Plan) {
        console.log(s3LifecarePlanZipUrlPath, 's3LifecarePlanZipUrlPaths3LifecarePlanZipUrlPath')
        await downloadCertifiedLifecarePlanVersion({
          s3UrlPath: s3LifecarePlanZipUrlPath,
          caseName,
          versionNumber,
          onDownloaded: onDownloadComplete,
        });
        return;
      }

      if (fileKey === DEMAND.Lifecare_Plan) {
        const client = lifecareClients.find((c) => c.clientId === lifecareClientId);
        await downloadLifecarePlanVersion({
          s3UrlPath: client?.s3FilePath,
          versionNumber,
          caseName,
          clientName: client?.clientName,
          onDownloaded: onDownloadComplete,
        });
        return;
      }

      const demandKey = DEMAND_TYPE?.[fileKey]?.key;
      const s3UrlPath = getDocxS3Url({ docxFiles, demandKey });
      const demandText = DEMAND_TYPE?.[fileKey]?.text;
      await downloadDocxVersion({
        s3UrlPath,
        versionNumber,
        fileName: `${caseName}-${demandText}.docx`,
        onDownloaded: onDownloadComplete,
      });
    } finally {
      onClose();
    }
  };

  const isLifecareSelected = selectedFileKey === DEMAND.Lifecare_Plan;
  const col2Versions =
    !isLifecareSelected && selectedFileKey
      ? versionsMap[selectedFileKey] || []
      : [];
  const col3Versions =
    isLifecareSelected && selectedLifecareClientId
      ? versionsMap[`lifecare_${selectedLifecareClientId}`] || []
      : [];

  return {
    isLoading,
    lifecareClients,
    demands,
    CASE_FILES_KEY,
    MEDICAL_CHRONOLOGY_KEY,
    SILENT_WITNESS_KEY,
    selectedFileKey,
    selectedLifecareClientId,
    setSelectedLifecareClientId,
    handleSelectFile,
    handleDownload,
    isLifecareSelected,
    col2Versions,
    col3Versions,
    formatVersionLabel,
  };
};
